import { useState, useCallback } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Button, Alert, Typography, IconButton, Box
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useUserMetadata } from '../../context/UserMetadataContext';
import { useAuth0Tokens } from '../../hooks/useAuth0Tokens';

const GitHubModal = ({ isOpen, onClose, onSuccess }) => {
  const { userMetadata } = useUserMetadata();
  const { getTokens } = useAuth0Tokens();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [formData, setFormData] = useState({
    orgName: '',
    accessToken: '',
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);

    const graphqlEndpoint = process.env.REACT_APP_GRAPHQL_ENDPOINT;

    if (!graphqlEndpoint) {
      setError('GraphQL endpoint configuration is missing');
      setIsLoading(false);
      return;
    }

    try {
      const { apiToken } = await getTokens();

      // Log GraphQL endpoint
      console.log('GraphQL Endpoint:', graphqlEndpoint);

      // Log the company name from metadata
      console.log('Company from metadata:', userMetadata?.company);

      // Validate required data
      if (!formData.orgName.trim()) {
        throw new Error('Organization name is required');
      }
      if (!formData.accessToken.trim()) {
        throw new Error('Access token is required');
      }
      if (!userMetadata?.company) {
        throw new Error('Company information is missing');
      }

      // GraphQL mutation
      const mutation = `
        mutation ConfigureGitHub($input: ConfigureGitHubInput!) {
          configureGitHub(input: $input) {
            success
            message
            integration {
              id
              tenantId
              integrationType
              orgName
              status
              createdAt
              updatedAt
            }
            errors
          }
        }
      `;

      const variables = {
        input: {
          orgName: formData.orgName.trim(),
          integrationToken: formData.accessToken.trim()
        }
      };

      console.log('GraphQL request:', { mutation, variables: { ...variables, input: { ...variables.input, integrationToken: '[REDACTED]' } } });

      const response = await fetch(graphqlEndpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${apiToken}`
        },
        body: JSON.stringify({ query: mutation, variables })
      });

      console.log('Response status:', response.status);

      const responseData = await response.json();
      console.log('GraphQL response:', responseData);

      // Check for GraphQL errors
      if (responseData.errors) {
        throw new Error(responseData.errors[0]?.message || 'GraphQL error occurred');
      }

      // Check mutation response
      const result = responseData.data?.configureGitHub;
      if (!result || !result.success) {
        throw new Error(result?.message || result?.errors?.[0] || 'Failed to configure GitHub integration');
      }

      // Success!
      onSuccess({
        status: 'success',
        organization: formData.orgName,
        message: 'GitHub integration successful',
        ...responseData
      });

      onClose();
    } catch (err) {
      // Enhanced error logging
      console.error('Integration error details:', {
        message: err.message,
        stack: err.stack
      });
      setError(err.message || 'An error occurred while connecting to GitHub');
    } finally {
      setIsLoading(false);
    }
  };

  const handleChange = useCallback((e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  }, []);

  const handleClose = useCallback(() => {
    setFormData({
      orgName: '',
      accessToken: ''
    });
    setError(null);
    onClose();
  }, [onClose]);

  return (
    <Dialog open={isOpen} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h6">Connect GitHub</Typography>
        <IconButton onClick={handleClose} size="small"><CloseIcon /></IconButton>
      </DialogTitle>
      <DialogContent>
        <Box component="form" id="github-form" onSubmit={handleSubmit}>
          <TextField
            fullWidth
            label="GitHub Organization Name"
            id="orgName"
            name="orgName"
            value={formData.orgName}
            onChange={handleChange}
            placeholder="Enter your GitHub organization name"
            required
            sx={{ mb: 2, mt: 1 }}
          />
          <TextField
            fullWidth
            label="GitHub Access Token"
            type="password"
            id="accessToken"
            name="accessToken"
            value={formData.accessToken}
            onChange={handleChange}
            placeholder="Enter your GitHub access token"
            required
            helperText="Generate a Personal Access Token with 'repo' and 'admin:org' scopes from GitHub Settings"
            sx={{ mb: 2 }}
          />
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={isLoading}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={isLoading}
        >
          {isLoading ? 'Connecting...' : 'Connect GitHub'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default GitHubModal;
